import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Alert,
  AlertTitle,
  Button
} from '@mui/material';
import {
  Refresh,
  Warning,
  Speed as SpeedIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { macroImpulseAPI } from '../services/api';
import {
  MacroImpulseSkeleton,
  SummaryCards,
  SectorsTable
} from '../components/macro-impulse';

const MacroImpulseDashboard = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'vi';
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await macroImpulseAPI.getLatest();
      setData(response.data);
    } catch (err) {
      console.error('Error fetching macro impulse data:', err);
      setError(err.response?.data?.detail || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const sortedScores = useMemo(() => {
    if (!data?.scores) return [];
    return [...data.scores].sort((a, b) => {
      const aScore = a.combined_score ?? a.impulse_score_0_100;
      const bScore = b.combined_score ?? b.impulse_score_0_100;
      return bScore - aScore;
    });
  }, [data?.scores]);

  const topPerformer = useMemo(() => sortedScores[0] || null, [sortedScores]);

  const averageScore = useMemo(() => {
    if (!data?.scores || data.scores.length === 0) return 0;
    const scores = data.scores.map(s => s.combined_score ?? s.impulse_score_0_100);
    return scores.reduce((sum, s) => sum + s, 0) / scores.length;
  }, [data?.scores]);

  const hotRunsCount = useMemo(() => {
    if (!data?.scores) return 0;
    return data.scores.filter(s => s.hot_run_flag).length;
  }, [data?.scores]);

  const overboughtCount = useMemo(() => {
    if (!data?.scores) return 0;
    return data.scores.filter(s => s.overbought_flag).length;
  }, [data?.scores]);

  const minScore = useMemo(() => {
    if (!data?.scores || data.scores.length === 0) return 0;
    const scores = data.scores.map(s => s.combined_score ?? s.impulse_score_0_100);
    return Math.min(...scores);
  }, [data?.scores]);

  const maxScore = useMemo(() => {
    if (!data?.scores || data.scores.length === 0) return 0;
    const scores = data.scores.map(s => s.combined_score ?? s.impulse_score_0_100);
    return Math.max(...scores);
  }, [data?.scores]);

  const totalIndicators = useMemo(() => {
    if (!data?.scores) return 0;
    return data.scores.reduce((sum, s) => sum + (s.indicator_count || 0), 0);
  }, [data?.scores]);

  const hotRunStats = useMemo(() => {
    if (!data?.scores) return { avgReturn: 0, avgScore: 0, avgVolume: 0 };
    const hotRuns = data.scores.filter(s => s.hot_run_flag);
    if (hotRuns.length === 0) return { avgReturn: 0, avgScore: 0, avgVolume: 0 };
    
    const avgReturn = hotRuns.reduce((sum, s) => {
      const ret = s.price_momentum?.ret_5d_pct ?? 0;
      return sum + ret;
    }, 0) / hotRuns.length;
    
    const avgScore = hotRuns.reduce((sum, s) => {
      const score = (s.combined_score ?? s.impulse_score_0_100) ?? 0;
      return sum + score;
    }, 0) / hotRuns.length;
    
    const avgVolume = hotRuns.reduce((sum, s) => {
      const vol = s.price_momentum?.vol_vs_5d_avg ?? 0;
      return sum + vol;
    }, 0) / hotRuns.length;
    
    return { avgReturn, avgScore, avgVolume };
  }, [data?.scores]);

  const overboughtStats = useMemo(() => {
    if (!data?.scores) return { avgReturn: 0, avgScore: 0, avgVolume: 0 };
    const overbought = data.scores.filter(s => s.overbought_flag);
    if (overbought.length === 0) return { avgReturn: 0, avgScore: 0, avgVolume: 0 };
    
    const avgReturn = overbought.reduce((sum, s) => {
      const ret = s.price_momentum?.ret_5d_pct ?? 0;
      return sum + ret;
    }, 0) / overbought.length;
    
    const avgScore = overbought.reduce((sum, s) => {
      const score = (s.combined_score ?? s.impulse_score_0_100) ?? 0;
      return sum + score;
    }, 0) / overbought.length;
    
    const avgVolume = overbought.reduce((sum, s) => {
      const vol = s.price_momentum?.vol_vs_5d_avg ?? 0;
      return sum + vol;
    }, 0) / overbought.length;
    
    return { avgReturn, avgScore, avgVolume };
  }, [data?.scores]);

  if (loading) {
    return <MacroImpulseSkeleton />;
  }

  if (error) {
    return (
      <Box sx={{ p: 4 }}>
        <Alert severity="error">
          <AlertTitle>{t('sectorMomentum.error')}</AlertTitle>
          {error}
        </Alert>
      </Box>
    );
  }

  const { data_maturity } = data;
  const showWarning = ['VERY_LOW', 'LOW'].includes(data_maturity?.maturity_level);

  return (
    <Box sx={{ width: '100%', px: 0, py: { xs: 1, sm: 2 } }}>
      {/* ═══════════════════════════════════════════════════════
          HEADER — Consistent with other pages
          ═══════════════════════════════════════════════════════ */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        {/* Left: Icon + Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 1.5,
            background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(245, 158, 11, 0.3)',
          }}>
            <SpeedIcon sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', letterSpacing: -0.3 }}>
                {t('sectorMomentum.title')}
              </Typography>
              {data?.scores?.[0]?.trade_date && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, bgcolor: '#f1f5f9', px: 1, py: 0.3, borderRadius: 1.5 }}>
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: '#f59e0b', boxShadow: '0 0 6px #f59e0b' }} />
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#475569' }}>
                    {data.scores[0].trade_date}
                  </Typography>
                </Box>
              )}
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>
              {t('sectorMomentum.subtitle')}
            </Typography>
          </Box>
        </Box>

        {/* Right: Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh sx={{ fontSize: 16 }} />}
            onClick={fetchData}
            sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '4px',
              color: '#64748b', borderColor: '#e2e8f0',
              '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' },
              '& .MuiButton-startIcon': { mr: 0.5 } }}
          >
            {t('sectorMomentum.refresh') || 'Refresh'}
          </Button>
        </Box>
      </Box>

      {/* Data Maturity Warning */}
      {showWarning && data_maturity && (
        <Alert 
          severity={data_maturity.maturity_level === 'VERY_LOW' ? 'error' : 'warning'}
          icon={<Warning />}
          sx={{ mb: 2.5, borderRadius: 2 }}
        >
          <AlertTitle>
            <strong>{t('sectorMomentum.dataMaturity')}: {data_maturity.maturity_level}</strong>
          </AlertTitle>
          {data_maturity.recommendation}
        </Alert>
      )}

      {/* Summary Cards */}
      <SummaryCards
        averageScore={averageScore}
        minScore={minScore}
        maxScore={maxScore}
        sortedScores={sortedScores}
        totalIndicators={totalIndicators}
        topPerformer={topPerformer}
        currentLang={currentLang}
        hotRunsCount={hotRunsCount}
        hotRunStats={hotRunStats}
        overboughtCount={overboughtCount}
        overboughtStats={overboughtStats}
      />

      {/* Sectors Table */}
      <SectorsTable
        sortedScores={sortedScores}
        currentLang={currentLang}
      />
    </Box>
  );
};

export default MacroImpulseDashboard;
